import React, { useState, useEffect, useCallback, useRef } from "react";
import { useThemeMode, ToggleSwitch, Alert, Spinner, TextInput, Button, Select } from "flowbite-react";
import { auth, database } from "../firebase";
import { ref, get, set, onValue } from "firebase/database";
import Chart from "./AutoTrades/Chart";
import Settings from "./AutoTrades/Settings";
import OpenPositions from "./AutoTrades/OpenPositions";

import { useCapitalCredentials } from "../hooks/useCapitalCredentials";
import { capitalApiClient } from "../api/CapitalApiClient";

const API_URLS = {
  LIVE: "https://api-capital.backend-capital.com/api/v1",
  DEMO: "https://demo-api-capital.backend-capital.com/api/v1",
};

const AutoSettings = () => {
  const [epicInput, setEpicInput] = useState("US100");
  const [currentEpic, setCurrentEpic] = useState("US100");
  const [error, setError] = useState(null);
  const [historicalPrices, setHistoricalPrices] = useState([]);
  const [atrPeriod, setAtrPeriod] = useState(20);
  const [profitMultiplier, setProfitMultiplier] = useState(2);
  const [stopMultiplier, setStopMultiplier] = useState(1);
  const [stopLevel, setStopLevel] = useState(0);
  const [profitLevel, setProfitLevel] = useState(0);
  const [autoTrades, setAutoTrades] = useState(false);
  const [selectedApiUrl, setSelectedApiUrl] = useState(API_URLS.LIVE);
  const [resolution, setResolution] = useState("MINUTE");
  const [webSocket, setWebSocket] = useState(null);
  const latestPriceRef = useRef(null);

  const chartRef = useRef(null);

  const { mode } = useThemeMode();
  const darkMode = mode === "dark";

  const { apiKey, identifier, password, loading: credentialsLoading, error: credentialsError } = useCapitalCredentials();

  const fetchSettings = useCallback(async () => {
    console.log("Fetching AutoTrades setting");
    const user = auth.currentUser;
    if (user) {
      const settingsRef = ref(database, `users/${user.uid}/settings`);
      const snapshot = await get(settingsRef);
      if (snapshot.exists()) {
        const settings = snapshot.val();
        setAtrPeriod(settings.ATR_PERIOD || 20);
        setProfitMultiplier(settings.PROFIT_MULTIPLIER || 2);
        setStopMultiplier(settings.STOP_MULTIPLIER || 1);
        setAutoTrades(settings.AUTO_TRADES || false);
      }
    }
  }, []);

  const handleAutoTradesToggle = async () => {
    const newAutoTradesValue = !autoTrades;
    setAutoTrades(newAutoTradesValue);
    const user = auth.currentUser;
    if (user) {
      const settingsRef = ref(database, `users/${user.uid}/settings`);
      await set(settingsRef, {
        ATR_PERIOD: atrPeriod,
        PROFIT_MULTIPLIER: profitMultiplier,
        STOP_MULTIPLIER: stopMultiplier,
        AUTO_TRADES: newAutoTradesValue,
      });
      console.log("AutoTrades setting saved successfully");
    }
  };

  const calculateStopAndProfitLevels = useCallback(
    (prices, direction = "BUY") => {
      if (prices.length === 0) return { stopLevel: 0, profitLevel: 0 };

      const currentPrice = prices[prices.length - 1].close;

      // Calculate ATR
      const trueRanges = prices.slice(1).map((price, i) => {
        const prevClose = prices[i].close;
        const high = price.high;
        const low = price.low;
        return Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
      });
      const atr = trueRanges.slice(-atrPeriod).reduce((sum, tr) => sum + tr, 0) / atrPeriod;

      // Calculate stop and profit distances as percentages of current price
      const stopDistance = currentPrice * (stopMultiplier / 100);
      const profitDistance = currentPrice * (profitMultiplier / 100);

      // Calculate stop and profit levels based on direction
      let stopLevel, profitLevel;
      if (direction === "BUY") {
        stopLevel = currentPrice - stopDistance;
        profitLevel = currentPrice + profitDistance;
      } else {
        stopLevel = currentPrice + stopDistance;
        profitLevel = currentPrice - profitDistance;
      }

      return {
        stopLevel: parseFloat(stopLevel.toFixed(5)),
        profitLevel: parseFloat(profitLevel.toFixed(5)),
        atr: parseFloat(atr.toFixed(5)),
        adjustedStopDistance: parseFloat(stopDistance.toFixed(5)),
        adjustedProfitDistance: parseFloat(profitDistance.toFixed(5)),
      };
    },
    [atrPeriod, stopMultiplier, profitMultiplier]
  );

  const fetchHistoricalPrices = useCallback(async () => {
    console.log("Fetching historical prices", resolution);
    try {
      const max = resolution.startsWith("MINUTE") ? 288 : 24; // Adjust max based on resolution

      const response = await capitalApiClient.request("GET", `/prices/${currentEpic}?resolution=${resolution}&max=${max}`);

      if (response && response.prices && response.prices.length > 0) {
        const prices = response.prices.map((price) => ({
          time: new Date(price.snapshotTimeUTC).getTime() / 1000,
          open: (price.openPrice.bid + price.openPrice.ask) / 2,
          high: Math.max(price.highPrice.ask, price.highPrice.bid),
          low: Math.min(price.lowPrice.ask, price.lowPrice.bid),
          close: (price.closePrice.bid + price.closePrice.ask) / 2,
        }));

        setHistoricalPrices(prices);
        setError(null);
      } else {
        console.warn("No price data received from API");
        setError("No price data available for the selected instrument");
      }
    } catch (error) {
      console.error("Error fetching historical prices:", error);
      setError(`Failed to fetch historical prices: ${error.message}`);
    }
  }, [currentEpic, resolution]);

  const saveSettings = async () => {
    const user = auth.currentUser;
    if (user) {
      const settingsRef = ref(database, `users/${user.uid}/settings`);
      await set(settingsRef, {
        ATR_PERIOD: atrPeriod,
        PROFIT_MULTIPLIER: profitMultiplier,
        STOP_MULTIPLIER: stopMultiplier,
        AUTO_TRADES: autoTrades,
      });
      console.log("Settings saved successfully");
    }
  };

  const setupWebSocket = useCallback(() => {
    if (webSocket) {
      webSocket.close();
    }
  
    const ws = new WebSocket("wss://api-streaming-capital.backend-capital.com/connect");
  
    ws.onopen = async () => {
      console.log("WebSocket connection established");
      try {
        const { cst, securityToken } = await capitalApiClient.getAuthTokens();
        subscribeToEpic(ws, currentEpic, cst, securityToken);
      } catch (error) {
        console.error("Error getting auth tokens:", error);
        setError("Failed to authenticate with Capital.com API");
      }
    };
  
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.status === "OK" && data.destination === "quote") {
        const { bid, ofr, timestamp } = data.payload;
        const price = (bid + ofr) / 2;
        latestPriceRef.current = price;
        updateChartWithLatestPrice(price, timestamp);
      }
    };
  
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setError("WebSocket connection error");
    };
  
    ws.onclose = () => {
      console.log("WebSocket connection closed");
      setTimeout(() => {
        console.log("Attempting to reconnect...");
        setupWebSocket();
      }, 5000);
    };
  
    setWebSocket(ws);
  }, [currentEpic]);


  const subscribeToEpic = (ws, epic, cst, securityToken) => {
    const subscribeMessage = {
      destination: "marketData.subscribe",
      correlationId: "1",
      cst: cst,
      securityToken: securityToken,
      payload: {
        epics: [epic],
      },
    };
    console.log(subscribeMessage)
    ws.send(JSON.stringify(subscribeMessage));
  };

  const unsubscribeFromEpic = (ws, epic, cst, securityToken) => {
    const unsubscribeMessage = {
      destination: "marketData.unsubscribe",
      correlationId: "2",
      cst: cst,
      securityToken: securityToken,
      payload: {
        epics: [epic],
      },
    };
    console.log(unsubscribeMessage)
    ws.send(JSON.stringify(unsubscribeMessage));
  };

  const updateChartWithLatestPrice = (price, timestamp) => {
    setHistoricalPrices((prevPrices) => {
      const lastPrice = prevPrices[prevPrices.length - 1];
      const currentTime = Math.floor(timestamp / 1000); // Convert milliseconds to seconds
      const resolutionInSeconds = getResolutionInSeconds(resolution);

      if (currentTime - lastPrice.time >= resolutionInSeconds) {
        // Add a new candle when the resolution period has passed
        const newCandle = {
          time: currentTime,
          open: price,
          high: price,
          low: price,
          close: price,
        };
        if (chartRef.current) {
          chartRef.current.updateChart(newCandle);
        }
        return [...prevPrices, newCandle];
      } else {
        const updatedLastPrice = {
          ...lastPrice,
          high: Math.max(lastPrice.high, price),
          low: Math.min(lastPrice.low, price),
          close: price,
        };
        if (chartRef.current) {
          chartRef.current.updateChart(updatedLastPrice);
        }
        return [...prevPrices.slice(0, -1), updatedLastPrice];
      }
    });
  };


  const handleResolutionChange = (e) => {
    setResolution(e.target.value);
  };

  const handlePositionClick = useCallback(
    (epic) => {
      setEpicInput(epic);
      setCurrentEpic(epic);
      fetchHistoricalPrices(epic);
    },
    [fetchHistoricalPrices]
  );

const handleUpdateEpic = useCallback(async () => {
  if (epicInput !== currentEpic) {
    try {
      const { cst, securityToken } = await capitalApiClient.getAuthTokens();
      
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        // Unsubscribe from the current epic
        unsubscribeFromEpic(webSocket, currentEpic, cst, securityToken);
        
        // Subscribe to the new epic
        subscribeToEpic(webSocket, epicInput, cst, securityToken);
      } else {
        // If there's no active WebSocket, set up a new one
        setupWebSocket();
      }

      setCurrentEpic(epicInput);
      setHistoricalPrices([]); // Clear existing historical prices
      await fetchHistoricalPrices();
    } catch (error) {
      console.error("Error updating epic:", error);
      setError("Failed to update epic. Please try again.");
    }
  }
}, [epicInput, currentEpic, setupWebSocket, fetchHistoricalPrices]);


  const getResolutionInSeconds = (resolution) => {
    switch (resolution) {
      case "MINUTE": return 60;
      case "MINUTE_5": return 300;
      case "MINUTE_15": return 900;
      case "MINUTE_30": return 1800;
      case "HOUR": return 3600;
      default: return 60;
    }
  };

  const resolutionOptions = [
    { value: "MINUTE", label: "1 Minute" },
    { value: "MINUTE_5", label: "5 Minutes" },
    { value: "MINUTE_15", label: "15 Minutes" },
    { value: "MINUTE_30", label: "30 Minutes" },
    { value: "HOUR", label: "1 Hour" },
  ];

  // useEffects

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (historicalPrices.length > 0) {
      const { stopLevel: newStopLevel, profitLevel: newProfitLevel } = calculateStopAndProfitLevels(historicalPrices);
      setStopLevel(newStopLevel);
      setProfitLevel(newProfitLevel);
    }
  }, [historicalPrices, atrPeriod, stopMultiplier, profitMultiplier, calculateStopAndProfitLevels]);

  useEffect(() => {
    if (apiKey && identifier && password && selectedApiUrl && currentEpic) {
      capitalApiClient.init(apiKey, identifier, password, selectedApiUrl);
      setHistoricalPrices([]); // Clear existing historical prices
      fetchHistoricalPrices().then(() => {
        setupWebSocket();
      });
    }
    return () => {
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        webSocket.close();
      }
    };
  }, [apiKey, identifier, password, selectedApiUrl, currentEpic, setupWebSocket, fetchHistoricalPrices])

  // chart when the resolution changes:
  // useEffect(() => {
  //   if (currentEpic) {
  //     fetchHistoricalPrices();
  //   }
  // }, [resolution, currentEpic, fetchHistoricalPrices]);



  if (credentialsLoading) return <Spinner aria-label="Loading credentials" />;
  if (credentialsError) return <Alert color="failure">{credentialsError}</Alert>;
  if (!apiKey || !identifier || !password) {
    return <Alert color="warning">Please set up your Capital.com API credentials in the Account settings.</Alert>;
  }

  return (
    <main className="h-full p-4 overflow-y-auto bg-slate-100 dark:bg-slate-900">
      <h2 className="text-2xl font-bold mb-4 text-slate-800 dark:text-slate-200">Historical Price Chart</h2>

      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <TextInput
            type="text"
            placeholder="Enter epic (e.g., US100)"
            value={epicInput}
            onChange={(e) => setEpicInput(e.target.value.toUpperCase())}
            className="mr-2"
          />
          <Button onClick={handleUpdateEpic}>Update Ticker</Button>
          <Select id="resolutionSelect" value={resolution} onChange={handleResolutionChange} className="ml-5 mr-2">
            {resolutionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center">
          {/* <Select id="apiUrlSelect" value={selectedApiUrl} onChange={handleApiUrlChange} className="mr-2">
            <option value={API_URLS.LIVE}>Live</option>
            <option value={API_URLS.DEMO}>Demo</option>
          </Select> */}
          <span className="mr-2 text-slate-800 dark:text-slate-200">Auto Trades</span>
          <ToggleSwitch checked={autoTrades} onChange={handleAutoTradesToggle} color="success" />
        </div>
      </div>

      {error && (
        <Alert color="failure" className="mb-4">
          {error}
        </Alert>
      )}

      <Chart
        ref={chartRef}
        historicalPrices={historicalPrices}
        darkMode={darkMode}
        stopLevel={stopLevel}
        profitLevel={profitLevel}
      />

      <Settings
        atrPeriod={atrPeriod}
        setAtrPeriod={setAtrPeriod}
        profitMultiplier={profitMultiplier}
        setProfitMultiplier={setProfitMultiplier}
        stopMultiplier={stopMultiplier}
        setStopMultiplier={setStopMultiplier}
        saveSettings={saveSettings}
      />
      {/* <OpenPositions onPositionClick={handlePositionClick} apiKey={apiKey} identifier={identifier} password={password} /> */}
    </main>
  );
};


export default AutoSettings;